import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, HandWaving } from "@phosphor-icons/react";

const IDLE_MS = 15 * 60 * 1000;      // 15 min of no activity -> "still there?"
const GRACE_MS = 3 * 60 * 1000;      // 3 more silent minutes -> clock pauses
const BEAT_MS = 60 * 1000;           // heartbeat to the server while active

const fmt = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(x).padStart(2, "0")}`;
};

export const TeamClock = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const tracked = ["team_content", "team_dev"].includes(user?.role);
  const [secs, setSecs] = useState(0);
  const [mode, setMode] = useState("active"); // active | prompt | paused
  const lastAct = useRef(Date.now());
  const modeRef = useRef("active");
  modeRef.current = mode;

  const beat = useCallback(async () => {
    try {
      const { data } = await api.post("/team/heartbeat", {});
      if (data?.session_seconds != null) setSecs(data.session_seconds);
    } catch { /* transient — next beat retries */ }
  }, []);

  // Page changes count as activity.
  useEffect(() => { if (tracked) lastAct.current = Date.now(); }, [loc.pathname, tracked]);

  useEffect(() => {
    if (!tracked) return undefined;
    const onAct = () => {
      if (modeRef.current === "paused") return; // paused needs an explicit key/click
      lastAct.current = Date.now();
      if (modeRef.current === "prompt") setMode("active");
    };
    const onResume = () => {
      if (modeRef.current !== "paused") return;
      lastAct.current = Date.now();
      setMode("active");
      beat();
    };
    const acts = ["pointermove", "pointerdown", "keydown", "wheel", "scroll"];
    acts.forEach((e) => window.addEventListener(e, onAct, { passive: true, capture: true }));
    document.addEventListener("timeupdate", onAct, true); // playing a video = active
    window.addEventListener("keydown", onResume, true);
    window.addEventListener("pointerdown", onResume, true);
    return () => {
      acts.forEach((e) => window.removeEventListener(e, onAct, { capture: true }));
      document.removeEventListener("timeupdate", onAct, true);
      window.removeEventListener("keydown", onResume, true);
      window.removeEventListener("pointerdown", onResume, true);
    };
  }, [tracked, beat]);

  useEffect(() => {
    if (!tracked) return undefined;
    beat();
    const tick = setInterval(() => {
      const idle = Date.now() - lastAct.current;
      if (modeRef.current === "active") {
        if (idle >= IDLE_MS) { setMode("prompt"); return; }
        setSecs((s) => s + 1);
      } else if (modeRef.current === "prompt" && idle >= IDLE_MS + GRACE_MS) {
        setMode("paused");
      }
    }, 1000);
    const b = setInterval(() => {
      if (modeRef.current === "active" && Date.now() - lastAct.current < IDLE_MS) beat();
    }, BEAT_MS);
    return () => { clearInterval(tick); clearInterval(b); };
  }, [tracked, beat]);

  if (!tracked) return null;
  return (
    <>
      <div data-testid="team-clock"
        className="fixed left-4 bottom-20 z-[60] flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black text-white shadow-lg select-none"
        title="Your tracked work time this session">
        <Clock size={14} weight="bold" color={mode === "active" ? "#CCFF00" : "#999"} />
        <span className="text-xs font-bold tabular-nums font-mono" data-testid="team-clock-time">{fmt(secs)}</span>
        {mode !== "active" && <span className="text-[10px] text-[#CCFF00] font-semibold ml-0.5">paused</span>}
      </div>
      {mode === "prompt" && (
        <div className="fixed inset-0 z-[210] bg-black/50 grid place-items-center p-4" data-testid="team-idle-prompt">
          <div className="bg-white rounded-2xl p-6 max-w-sm text-center shadow-xl">
            <HandWaving size={34} weight="duotone" color="#CCFF00" className="mx-auto mb-2" />
            <div className="font-display font-extrabold text-lg">It looks as if you've left — are you still there?</div>
            <p className="text-sm text-[#555] mt-1">Your time clock will pause shortly if there's no activity.</p>
            <button className="btn-primary mt-4" data-testid="team-idle-still-here"
              onClick={() => { lastAct.current = Date.now(); setMode("active"); }}>
              I'm still here
            </button>
          </div>
        </div>
      )}
      {mode === "paused" && (
        <div className="fixed inset-0 z-[210] bg-black/60 grid place-items-center p-4" data-testid="team-idle-paused">
          <div className="bg-white rounded-2xl p-6 max-w-sm text-center shadow-xl">
            <Clock size={34} weight="duotone" color="#999" className="mx-auto mb-2" />
            <div className="font-display font-extrabold text-lg">Thank you — hit any key to let me know when you're back.</div>
            <p className="text-sm text-[#555] mt-1">The clock has stopped counting. It resumes the moment you press a key or click.</p>
          </div>
        </div>
      )}
    </>
  );
};
